// File: SchedulesCacheContext.tsx
import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import ApiClient from '../utils/apiClient';
import type { Schedule } from '../types';

/** Cache-Eintrag pro Student. */
export interface StudentSchedulesEntry {
  schedules: Schedule[];
  member_ids: string[];
  classes_with_primary: unknown[];
}

export interface SchedulesCacheContextType {
  loadSchedulesForClass: (classId: string) => Promise<void>;
  getSchedulesForStudent: (userId: string) => StudentSchedulesEntry | null;
  hasSchedulesForStudent: (userId: string) => boolean;
  loadSchedulesForStudent: (userId: string) => Promise<StudentSchedulesEntry>;
  clearCache: () => void;
  isLoading: boolean;
  isLoaded: boolean;
}

const SchedulesCacheContext = createContext<SchedulesCacheContextType | undefined>(undefined);

/**
 * Provider für den Schedule-Cache
 * Verwaltet alle Student-Schedules für eine Klasse zentral und vermeidet redundante API-Calls
 */
export function SchedulesCacheProvider({ children }: { children: ReactNode }) {
  // Cache-Struktur: { [user_id]: { schedules: [...], member_ids: [...], classes_with_primary: [...] } }
  const [cache, setCache] = useState<Record<string, StudentSchedulesEntry>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadedClassId, setLoadedClassId] = useState<string | null>(null);

  /**
   * Lädt alle Schedules für alle registrierten Studenten einer Klasse
   */
  const loadSchedulesForClass = useCallback(async (classId: string): Promise<void> => {
    if (!classId) {
      console.warn('[SchedulesCacheContext] No classId provided');
      return;
    }

    // Prüfe ob bereits für diese Klasse geladen
    if (loadedClassId === classId) {
      console.log('[SchedulesCacheContext] Schedules already loaded for this class');
      return;
    }

    try {
      setIsLoading(true);
      console.log(`[SchedulesCacheContext] Loading schedules for class ${classId}...`);

      const response = await ApiClient.get(`classes/${classId}/students/schedules`);
      // ApiClient wirft bei !ok - kein manueller Check nötig
      const data = await response.json();
      const studentsSchedules: Record<string, StudentSchedulesEntry> = data.students_schedules || {};

      console.log(`[SchedulesCacheContext] Loaded schedules for ${Object.keys(studentsSchedules).length} students`);

      setCache(studentsSchedules);
      setLoadedClassId(classId);
    } catch (caught) {
      const error = caught as Error;
      if (error.name !== 'AbortError') {
        console.error('[SchedulesCacheContext] Error loading schedules:', error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [loadedClassId]);

  /**
   * Holt die Schedules für einen bestimmten Studenten aus dem Cache
   */
  const getSchedulesForStudent = useCallback((userId: string): StudentSchedulesEntry | null => {
    if (!userId) {
      return null;
    }

    const result = cache[userId];

    if (result) {
      console.log(`[SchedulesCacheContext] Cache hit for student ${userId}: ${result.schedules.length} schedules`);
    } else {
      console.log(`[SchedulesCacheContext] Cache miss for student ${userId}`);
    }

    return result || null;
  }, [cache]);

  /**
   * Prüft ob Schedules für einen Studenten im Cache sind
   */
  const hasSchedulesForStudent = useCallback((userId: string): boolean => {
    return Boolean(userId && cache[userId] !== undefined);
  }, [cache]);

  /**
   * Löscht den Cache (z.B. beim Klassenwechsel)
   */
  const clearCache = useCallback(() => {
    console.log('[SchedulesCacheContext] Clearing cache');
    setCache({});
    setLoadedClassId(null);
  }, []);

  /**
   * Lädt die Schedules für einen einzelnen Studenten nach (Fallback)
   */
  const loadSchedulesForStudent = useCallback(async (userId: string): Promise<StudentSchedulesEntry> => {
    if (!userId) {
      return { schedules: [], member_ids: [], classes_with_primary: [] };
    }

    // Prüfe Cache zuerst
    if (cache[userId]) {
      console.log(`[SchedulesCacheContext] Returning cached data for student ${userId}`);
      return cache[userId];
    }

    try {
      console.log(`[SchedulesCacheContext] Loading schedules for individual student ${userId}...`);
      const response = await ApiClient.get(`students/${userId}/schedules`);
      // ApiClient wirft bei !ok - kein manueller Check nötig
      const data = await response.json();
      const result: StudentSchedulesEntry = {
        schedules: data.schedules || [],
        member_ids: data.member_ids || [],
        classes_with_primary: data.classes_with_primary || []
      };

      // Cache speichern
      setCache(prev => ({
        ...prev,
        [userId]: result
      }));

      return result;
    } catch (caught) {
      const error = caught as Error;
      if (error.name !== 'AbortError') {
        console.error(`[SchedulesCacheContext] Error loading schedules for student ${userId}:`, error);
      }
      return { schedules: [], member_ids: [], classes_with_primary: [] };
    }
  }, [cache]);

  const value: SchedulesCacheContextType = {
    loadSchedulesForClass,
    getSchedulesForStudent,
    hasSchedulesForStudent,
    loadSchedulesForStudent,
    clearCache,
    isLoading,
    isLoaded: loadedClassId !== null
  };

  return (
    <SchedulesCacheContext.Provider value={value}>
      {children}
    </SchedulesCacheContext.Provider>
  );
}

/**
 * Hook zum Zugriff auf den Schedule-Cache
 */
export function useSchedulesCache(): SchedulesCacheContextType {
  const context = useContext(SchedulesCacheContext);
  if (!context) {
    throw new Error('useSchedulesCache must be used within a SchedulesCacheProvider');
  }
  return context;
}
